/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, IPayLoad, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import classnames from 'classnames';

import { IRefinerItemData, RefinerItemType } from './common';

/**
 * Class names used in component.
 */
const componentClassName: string = 'msc-refiner-choice-summary';
const labelClassName: string = `${componentClassName}__label`;
const listClassName: string = `${componentClassName}__list`;
const listItemClassName: string = `${componentClassName}__list-item`;
const itemClassName: string = `${componentClassName}__item`;
const closeButtonGlyphClassName: string = `${componentClassName}__close-btn`;
const buttonGlyphClassName: string = `${componentClassName}__glyph`;
const clearAllButtonClassName: string = `${componentClassName}__clear-all`;

/**
 * IRefinerChoiceSummaryClickNotification interface.
 */
export interface IRefinerChoiceSummaryClickNotification {
    selectedRefinerItem: IRefinerItemData | undefined;
    shouldClearAll: boolean;
    nextItemToFocus: HTMLElement | undefined;
}

/**
 * IRefinerChoiceSummaryComponent interface.
 */
interface IRefinerChoiceSummaryComponent extends IComponent<IRefinerChoiceSummaryProps> {}

/**
 * IRefinerChoiceSummaryData interface.
 */
export interface IRefinerChoiceSummaryData {
    selectedChoices: IRefinerItemData[];
}

/**
 * IRefinerChoiceSummaryProps interface.
 */
export interface IRefinerChoiceSummaryProps extends IComponentProps<IRefinerChoiceSummaryData> {
    className?: string;
    clearAllText?: string;
    label?: string;
    choiceAriaLabel?: string;
    choiceFormat?: string;
    closeAriaLabel?: string;
    telemetryContent?: ITelemetryContent;
    onClickChoice(notification: IRefinerChoiceSummaryClickNotification): void;
    onClickClearAll(notification: IRefinerChoiceSummaryClickNotification): void;
}

/**
 * RefinerChoiceSummary component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const RefinerChoiceSummaryFunctionalComponent: React.FC<IRefinerChoiceSummaryProps> = (props: IRefinerChoiceSummaryProps) => {
    const {
        className,
        clearAllText,
        label,
        choiceAriaLabel,
        choiceFormat,
        closeAriaLabel,
        data: { selectedChoices },
        onClickChoice,
        onClickClearAll
    } = props;

    /**
     * Get choice summary name.
     * @param activeRefinerItem - Active refine Item data.
     * @returns - Returns the tag name.
     */
    const getTagName = (activeRefinerItem: IRefinerItemData): string => {
        /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- Required for string.  */
        const overallFormat = choiceFormat ?? '{0}';

        const refinerLabel: string = activeRefinerItem.label;
        let refinerValue: string = activeRefinerItem.value ?? '';
        if (activeRefinerItem.itemType === RefinerItemType.DateRange) {
            const refinerMinValue: string = activeRefinerItem.minValue ?? '';
            const refinerMaxValue: string = activeRefinerItem.maxValue ?? '';
            refinerValue = '{0} - {1}'.replace('{0}', refinerMinValue).replace('{1}', refinerMaxValue);
        }

        return overallFormat.replace('{0}', refinerLabel).replace('{1}', refinerValue);
        /* eslint-enable @typescript-eslint/prefer-nullish-coalescing -- Required for string.  */
    };

    const onClickChoiceHandler = React.useCallback(
        (selectedRefinerItem: IRefinerItemData) => (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
            event.preventDefault();
            event.stopPropagation();

            const targetChoiceItem = event.currentTarget as HTMLElement;

            onClickChoice({
                selectedRefinerItem,
                shouldClearAll: false,
                nextItemToFocus: targetChoiceItem.nextSibling as HTMLElement
            });
        },
        [onClickChoice]
    );

    const onClickClearAllHandler = React.useCallback(
        () => (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
            event.preventDefault();
            event.stopPropagation();

            const targetChoiceItem = event.currentTarget as HTMLElement;

            onClickClearAll({
                selectedRefinerItem: undefined,
                shouldClearAll: true,
                nextItemToFocus: targetChoiceItem.nextSibling as HTMLElement
            });
        },
        [onClickClearAll]
    );

    // define telemetry attributes
    const payLoad: IPayLoad = getPayloadObject('click', props.telemetryContent!, '');
    payLoad.contentAction.etext = clearAllText;
    const clearAllAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);

    return (
        <div className={componentClassName}>
            {ArrayExtensions.hasElements(selectedChoices) && label && <span className={labelClassName}>{label}</span>}
            <ul className={classnames(className, listClassName, 'list-unstyled')}>
                {ArrayExtensions.hasElements(selectedChoices) &&
                    selectedChoices.map((choiceItem, index) => {
                        const choiceItemName = getTagName(choiceItem);

                        // define telemetry attributes
                        payLoad.contentAction.etext = choiceItemName;
                        const choiceItemAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);

                        return (
                            <li className={listItemClassName} key={index}>
                                <a
                                    key={choiceItem.refinerItemId}
                                    className={itemClassName}
                                    role='button'
                                    aria-label={`${choiceItemName} ${choiceAriaLabel ?? ''}`}
                                    onClick={onClickChoiceHandler(choiceItem)}
                                    {...choiceItemAttribute}
                                >
                                    {choiceItemName}
                                    <span
                                        className={classnames(closeButtonGlyphClassName, buttonGlyphClassName)}
                                        role='button'
                                        aria-label={closeAriaLabel}
                                    />
                                </a>
                            </li>
                        );
                    })}
            </ul>
            {ArrayExtensions.hasElements(selectedChoices) && clearAllText && (
                <a
                    className={clearAllButtonClassName}
                    aria-label={closeAriaLabel}
                    onClick={onClickClearAllHandler()}
                    {...clearAllAttributes}
                >
                    {clearAllText}
                </a>
            )}
        </div>
    );
};

/**
 * RefinerChoiceSummary component.
 */
export const RefinerChoiceSummaryComponent: React.FunctionComponent<IRefinerChoiceSummaryProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error -- Required for this signature.
    IRefinerChoiceSummaryComponent
>('Refiner-Choice-Summary', { component: React.memo(RefinerChoiceSummaryFunctionalComponent) });
